/* eslint-disable jsx-a11y/alt-text */
import { AppCSS, Spacer, TapButton } from "../../../../components";
import { Prompt } from "../../../../components/dialogue/Dialogue";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { Docs } from "../../../../redux/types";
import { useMutation } from "@apollo/client";
import { ARCHIVE_PROJECT } from "../../../../apolloClient/operation/mutations";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import styled from "@emotion/styled";

interface CustomerProjectProps {
  project: CustomerProjectDetails;
  docs?: Docs[];
  onDelete: Function;
}

const ARCHIVE_REASONS = [
  { label: "Do not contact", value: "DoNotContact" },
  { label: "Customer stopped communicating", value: "CustomerStoppedCommunicating" },
  { label: "Project completed", value: "ProjectCompleted" },
  { label: "Unable to collect", value: "UnableToCollect" },
  { label: "Cancelled project", value: "CancelledProject" },
  { label: "Unserviceable Request", value: "UnserviceableRequest" },
  { label: "Test Project", value: "TestProject" },
  { label: "Other", value: "Other" },
];

const Card = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  padding: 40px 32px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Title = styled.h2`
  color: #d32f2f;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: center;
`;

const StyledLabel = styled.label`
  color: #333;
  font-weight: 500;
  font-size: 1rem;
  margin-bottom: 8px;
  margin-top: 24px;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1.5px solid #bdbdbd;
  background: #fafafa;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 14px;
  transition: border 0.2s;
  &:focus {
    border-color: #1976d2;
    outline: none;
    background: #fff;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1.5px solid #bdbdbd;
  background: #fafafa;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 24px;
  transition: border 0.2s;
  &:focus {
    border-color: #1976d2;
    outline: none;
    background: #fff;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1.5px solid #bdbdbd;
  background: #fafafa;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 24px;
  resize: vertical;
  min-height: 72px;
  max-height: 160px;
  transition: border 0.2s;
  &:focus {
    border-color: #1976d2;
    outline: none;
    background: #fff;
  }
`;

const ActionButton = styled.button`
  height: 48px;
  width: 100%;
  border-radius: 6px;
  border: none;
  font-size: 1.1rem;
  font-weight: 700;
  background: #d32f2f;
  color: #fff;
  margin-top: 8px;
  box-shadow: 0 2px 8px rgba(211, 47, 47, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  &:hover, &:focus {
    filter: brightness(0.9);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const InfoText = styled.div`
  color: #666;
  font-size: 1rem;
  margin-bottom: 16px;
  text-align: center;
`;

export const DeleteProject: FC<CustomerProjectProps> = ({
  project,
  docs,
  onDelete,
}) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [archiveReason, setArchiveReason] = useState<string>("DoNotContact");
  const [otherReason, setOtherReason] = useState<string>("");

  const [archiveProject, { loading: archiving }] = useMutation<
    { ArchiveProject: boolean },
    { pId: number; reason: string; customReason?: string; archive: boolean }
  >(ARCHIVE_PROJECT);

  const isArchived = project?.Archived || false;

  const onAction = async () => {
    setOpenConfirm(false);
    if (!project) {
      toast(`Project can not be ${isArchived ? 'unarchived' : 'archived'}!`, { type: "error" });
      return;
    }

    // Only require reason when archiving, not when unarchiving
    if (!isArchived && archiveReason === "Other" && !otherReason.trim()) {
      toast("Specify the reason for archiving.", { type: "error" });
      return;
    }

    const variables: { pId: number; reason: string; customReason?: string; archive: boolean } = {
      pId: project.projectId,
      reason: archiveReason,
      archive: !isArchived, // Toggle the archive status
    };

    if (!isArchived && archiveReason === "Other") {
      variables.customReason = otherReason;
    }

    try {
      await archiveProject({
        variables,
      });
      toast(`Successfully ${isArchived ? 'unarchived' : 'archived'} project.`, { type: "success" });
      
      // Track project archiving (only when archiving, not unarchiving)
      if (!isArchived) {
        const reason = archiveReason === "Other" ? otherReason : archiveReason;
        logEvent(PROJECT_EVENTS.PROJECT_ARCHIVED, { 
          project_id: project.projectId, 
          reason 
        });
      }
      
      if (onDelete) onDelete();
    } catch (error) {
      toast(`Error ${isArchived ? 'unarchiving' : 'archiving'} project`, { type: "error" });
    }
  };

  const getActionLabel = () => {
    if (archiving) {
      return isArchived ? "Unarchiving..." : "Archiving...";
    }
    return isArchived ? "Unarchive Project" : "Archive Project";
  };

  const getConfirmMessage = () => {
    if (isArchived) {
      return "Are you sure you want to unarchive this project?";
    }

    const reasonLabel =
      archiveReason === "Other"
        ? `Other: ${otherReason}`
        : ARCHIVE_REASONS.find(r => r.value === archiveReason)?.label ?? "";
    return `Are you sure you want to archive this project for reason: ${reasonLabel}?`;
  };

  return (
    <>
      <Prompt
        message={getConfirmMessage()}
        onConfirm={onAction}
        onClose={() => setOpenConfirm(false)}
        open={openConfirm}
      />
      <Card>
        <Title>{isArchived ? "Unarchive Project" : "Danger Zone"}</Title>
        <InfoText className="text-wrap">
          {isArchived
            ? "Click the button below to unarchive this project and make it active again."
            : "Please select a reason before archiving this project."
          }
        </InfoText>

        {!isArchived && (
          <>
            <StyledLabel htmlFor="archive-reason">Reason</StyledLabel>
            <StyledSelect
              id="archive-reason"
              value={archiveReason}
              onChange={e => setArchiveReason(e.target.value)}
            >
              {ARCHIVE_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </StyledSelect>
            {archiveReason === "Other" && (
              <>
                <StyledLabel htmlFor="other-reason">Please specify</StyledLabel>
                <StyledTextarea
                  id="other-reason"
                  rows={4}
                  value={otherReason}
                  onChange={e => setOtherReason(e.target.value)}
                  placeholder="Enter reason"
                />
              </>
            )}
          </>
        )}

        <ActionButton
          onClick={() => setOpenConfirm(true)}
          disabled={archiving}
          style={{
            background: isArchived ? "#1976d2" : "#d32f2f",
          }}
        >
          {getActionLabel()}
        </ActionButton>
      </Card>
    </>
  );
};
